import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  ButtonGroup,
  Autocomplete,
  Chip,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  AutoFixHigh as AIIcon,
  Psychology as RagIcon,
  TextFields as TextFieldsIcon,
} from '@mui/icons-material';
import { dokumenteApi, adressenApi, vorlagenApi, llmApi, textbausteineApi } from '../services/api';
import { ragService } from '../services/ragService';
import type { Adresse, Vorlage, Textbaustein } from '../types/api';
import VoiceInputButton from '../components/VoiceInputButton';

const DokumentNeu: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // URL-Parameter auslesen (auch von AI Chat)
  const vorlagenIdParam = searchParams.get('vorlageId');
  const typParam = searchParams.get('typ');
  const adresseIdParam = searchParams.get('adresseId');
  const betreffParam = searchParams.get('betreff');
  const inhaltParam = searchParams.get('inhalt');
  const vorlageNameParam = searchParams.get('vorlage'); // Exakter Vorlagenname von AI Chat

  const [adressen, setAdressen] = useState<Adresse[]>([]);
  const [vorlagen, setVorlagen] = useState<Vorlage[]>([]);
  const [textbausteine, setTextbausteine] = useState<Textbaustein[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [textbausteinLoading, setTextbausteinLoading] = useState(false);

  // Form fields - mit URL-Parametern initialisieren
  const [betreff, setBetreff] = useState(betreffParam || '');
  const [adresseID, setAdresseID] = useState<number | undefined>(
    adresseIdParam ? parseInt(adresseIdParam) : undefined
  );
  const [fusszeile, setFusszeile] = useState('');
  const [inhalt, setInhalt] = useState(inhaltParam || '');
  const [selectedVorlageId, setSelectedVorlageId] = useState<number | undefined>(
    vorlagenIdParam ? parseInt(vorlagenIdParam) : undefined
  );
  const [editorTyp, setEditorTyp] = useState<'OnlyOffice' | 'Syncfusion'>('Syncfusion');
  const [dokumentTyp, setDokumentTyp] = useState<'Brief' | 'Email' | 'Excel'>('Brief');

  // KI-Enhancement states
  const [aiEnhancing, setAiEnhancing] = useState(false);
  const [ragEnhancing, setRagEnhancing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Vorlage automatisch wählen basierend auf vorlage- oder typ-Parameter
  // Priorität: 1. Exakter Vorlagenname (vorlage), 2. Typ-basiert (typ)
  useEffect(() => {
    if (vorlagen.length > 0 && !selectedVorlageId) {
      // 1. Priorität: Exakter Vorlagenname (von AI Chat)
      if (vorlageNameParam) {
        // Versuche exakten Match (case-insensitive)
        let matchingVorlage = vorlagen.find(v =>
          v.vorlageName?.toLowerCase() === vorlageNameParam.toLowerCase()
        );
        // Falls kein exakter Match, versuche Teil-Match
        if (!matchingVorlage) {
          matchingVorlage = vorlagen.find(v =>
            v.vorlageName?.toLowerCase().includes(vorlageNameParam.toLowerCase())
          );
        }
        if (matchingVorlage) {
          setSelectedVorlageId(matchingVorlage.vorlageID);
          console.log('Vorlage nach exaktem Namen gewählt:', matchingVorlage.vorlageName);
          return;
        }
      }

      // 2. Priorität: Typ-basierte Auswahl
      if (typParam) {
        const matchingVorlage = vorlagen.find(v =>
          v.vorlageName?.toLowerCase().includes(typParam.toLowerCase())
        );
        if (matchingVorlage) {
          setSelectedVorlageId(matchingVorlage.vorlageID);
          console.log('Vorlage nach Typ gewählt:', matchingVorlage.vorlageName);
        }
      }
    }
  }, [vorlageNameParam, typParam, vorlagen, selectedVorlageId]);

  // Betreff nur aus Vorlage setzen wenn kein Betreff aus URL kommt
  useEffect(() => {
    if (selectedVorlageId && vorlagen.length > 0 && !betreffParam) {
      const vorlage = vorlagen.find(v => v.vorlageID === selectedVorlageId);
      if (vorlage && !betreff) {
        setBetreff(vorlage.vorlageName || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVorlageId, vorlagen, betreffParam]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [adressenResponse, vorlagenResponse, textbausteineResponse] = await Promise.all([
        adressenApi.getAll({ take: 500 }),
        vorlagenApi.getAll(),
        textbausteineApi.getAll(),
      ]);

      setAdressen(adressenResponse.data);
      setVorlagen(vorlagenResponse.data);
      setTextbausteine(textbausteineResponse.data);
    } catch (err: any) {
      console.error('Fehler beim Laden:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  // KI-Verbesserung mit Standard-LLM
  const handleAIEnhance = async () => {
    if (!inhalt.trim()) {
      alert('Bitte geben Sie zuerst einen Text ein, der verbessert werden soll.');
      return;
    }

    try {
      setAiEnhancing(true);

      // Empfängerdaten aus der ausgewählten Adresse extrahieren
      const selectedAdresse = adresseID ? adressen.find(a => a.adresseID === adresseID) : undefined;

      const response = await llmApi.improveText({
        originalText: inhalt,
        verbesserungsziel: 'Verbessere den folgenden Brieftext: Mache ihn professioneller, klarer und formeller. Behalte den Inhalt bei, aber optimiere Grammatik, Stil und Ausdrucksweise.',
        empfaengerAnrede: selectedAdresse?.anrede,
        empfaengerTitel: selectedAdresse?.titel,
        empfaengerVorname: selectedAdresse?.vorname,
        empfaengerNachname: selectedAdresse?.nachname,
        empfaengerFirma: selectedAdresse?.firma
      });

      if (response.data.text) {
        setInhalt(response.data.text);
      }
    } catch (err: any) {
      alert('Fehler bei KI-Verbesserung: ' + (err.response?.data?.message || err.message));
    } finally {
      setAiEnhancing(false);
    }
  };

  // KI-Verbesserung mit AnythingLLM (RAG)
  const handleRagEnhance = async () => {
    if (!inhalt.trim()) {
      alert('Bitte geben Sie zuerst einen Text ein, der verbessert werden soll.');
      return;
    }

    try {
      setRagEnhancing(true);
      const response = await ragService.chat({
        message: `Verbessere den folgenden Brieftext basierend auf deinem Wissen. Mache ihn professioneller und stelle sicher, dass er zu unserem Unternehmensstil passt. Behalte den Inhalt bei:\n\n${inhalt}`,
        mode: 'chat'
      });

      if (response.success && response.response) {
        setInhalt(response.response);
      } else if (response.error) {
        alert('AnythingLLM Fehler: ' + response.error);
      }
    } catch (err: any) {
      alert('Fehler bei RAG-Verbesserung: ' + (err.response?.data?.message || err.message));
    } finally {
      setRagEnhancing(false);
    }
  };

  // Textbaustein einfügen
  const handleInsertTextbaustein = async (textbaustein: Textbaustein | null) => {
    if (!textbaustein) return;

    try {
      setTextbausteinLoading(true);
      let content = '';

      // Check if AI enhancement is enabled for this textbaustein
      if (textbaustein.kiVerbessern) {
        try {
          const response = await textbausteineApi.enhanceWithAI(textbaustein.bausteinID);
          content = response.data.content || '';
        } catch (aiErr: any) {
          setTextbausteinLoading(false);
          alert('Fehler bei der KI-Verbesserung: ' + (aiErr.response?.data?.message || aiErr.message));
          return;
        }
      } else {
        // Fetch the full content of the textbaustein without AI enhancement
        const response = await textbausteineApi.getContent(textbaustein.bausteinID);
        content = response.data.content || '';
      }

      // Füge Textbaustein zum Inhalt hinzu
      setInhalt((prevInhalt) => {
        if (!prevInhalt.trim()) {
          return content;
        }
        return prevInhalt + '\n\n' + content;
      });
    } catch (err: any) {
      alert('Fehler beim Laden des Textbausteins: ' + (err.response?.data?.message || err.message));
    } finally {
      setTextbausteinLoading(false);
    }
  };

  const handleSave = async () => {
    if (!betreff.trim()) {
      alert('Bitte geben Sie einen Betreff ein.');
      return;
    }

    if (!selectedVorlageId) {
      alert('Bitte wählen Sie eine Vorlage aus.');
      return;
    }

    try {
      setSaving(true);
      const requestData = {
        betreff: betreff,
        adresseID: adresseID,
        vorlageID: selectedVorlageId,
        dokumentTyp: dokumentTyp,
        fusszeile: fusszeile,
        editorTyp: editorTyp,
        inhalt: inhalt || undefined,
      };
      console.log('Dokument erstellen mit:', requestData);
      const response = await dokumenteApi.generate(requestData);

      const newDokumentId = response.data.dokumentID;

      // Alle Dokumente (Word, Excel) zur Detailseite navigieren für Bearbeitung
      alert('Dokument erfolgreich erstellt!');
      navigate(`/dokumente/${newDokumentId}`);
    } catch (err: any) {
      alert('Fehler beim Erstellen: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100vh" position="fixed" top={0} left={0}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', height: '100vh', p: 3, position: 'fixed', top: 0, left: 0 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dokumente')}>
          Zurück zur Übersicht
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'fixed', top: 0, left: 0, backgroundColor: 'background.default' }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: 3,
          py: 2,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dokumente')}>
            Zurück
          </Button>
          <Typography variant="h5">Neues Dokument erstellen</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !betreff.trim()}
          >
            {saving ? 'Erstelle...' : 'Erstellen'}
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ display: 'flex', flexGrow: 1, width: '100%', overflow: 'hidden' }}>
        {/* Left Side: Form */}
        <Box sx={{ flex: '1 1 75%', height: '100%', borderRight: '1px solid', borderColor: 'divider', minWidth: 0, overflow: 'auto' }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Dokumenteinstellungen</Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              Wählen Sie eine Vorlage und eine Adresse aus. Das Dokument wird basierend auf der Vorlage generiert und kann anschließend bearbeitet werden.
            </Alert>

            <FormControl fullWidth sx={{ mb: 3 }} required>
              <InputLabel>Vorlage *</InputLabel>
              <Select
                value={selectedVorlageId || ''}
                label="Vorlage *"
                onChange={(e) => setSelectedVorlageId(e.target.value ? Number(e.target.value) : undefined)}
              >
                <MenuItem value="">
                  <em>Vorlage auswählen...</em>
                </MenuItem>
                {vorlagen.map((vorlage) => (
                  <MenuItem key={vorlage.vorlageID} value={vorlage.vorlageID}>
                    {vorlage.vorlageName}
                    {vorlage.beschreibung && ` - ${vorlage.beschreibung}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Dokumenttyp</InputLabel>
              <Select
                value={dokumentTyp}
                label="Dokumenttyp"
                onChange={(e) => setDokumentTyp(e.target.value as 'Brief' | 'Email' | 'Excel')}
              >
                <MenuItem value="Brief">Brief</MenuItem>
                <MenuItem value="Email">E-Mail</MenuItem>
                <MenuItem value="Excel">Excel</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Editor</InputLabel>
              <Select
                value={editorTyp}
                label="Editor"
                onChange={(e) => setEditorTyp(e.target.value as 'OnlyOffice' | 'Syncfusion')}
              >
                <MenuItem value="Syncfusion">
                  Syncfusion - Word-Editor, Textbausteine direkt einfügen
                </MenuItem>
                <MenuItem value="OnlyOffice">
                  OnlyOffice - Volle Word-Kompatibilität (externer Server)
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Adresse (Empfänger)</InputLabel>
              <Select
                value={adresseID || ''}
                label="Adresse (Empfänger)"
                onChange={(e) => setAdresseID(e.target.value ? Number(e.target.value) : undefined)}
              >
                <MenuItem value="">
                  <em>Keine Adresse</em>
                </MenuItem>
                {adressen.map((adresse) => (
                  <MenuItem key={adresse.adresseID} value={adresse.adresseID}>
                    {adresse.vollstaendigerName}
                    {adresse.firma && ` (${adresse.firma})`}
                    {adresse.ort && ` - ${adresse.ort}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Betreff"
              value={betreff}
              onChange={(e) => setBetreff(e.target.value)}
              required
              sx={{ mb: 3 }}
            />

            {/* Großes Textfeld für Briefinhalt */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Briefinhalt (wird in {'{{HAUPTTEXT}}'} eingefügt)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VoiceInputButton
                    onTranscription={(text) => {
                      setInhalt((prev) => prev ? prev + ' ' + text : text);
                    }}
                    size="small"
                  />
                  <ButtonGroup size="small" variant="outlined">
                    <Tooltip title="Text mit KI verbessern (Standard-LLM)">
                      <Button
                        onClick={handleAIEnhance}
                        disabled={aiEnhancing || ragEnhancing || !inhalt.trim()}
                        startIcon={aiEnhancing ? <CircularProgress size={16} /> : <AIIcon />}
                      >
                        {aiEnhancing ? 'Verbessere...' : 'KI'}
                      </Button>
                    </Tooltip>
                    <Tooltip title="Text mit AnythingLLM verbessern (Wissensdatenbank)">
                      <Button
                        onClick={handleRagEnhance}
                        disabled={aiEnhancing || ragEnhancing || !inhalt.trim()}
                        startIcon={ragEnhancing ? <CircularProgress size={16} /> : <RagIcon />}
                      >
                        {ragEnhancing ? 'Verbessere...' : 'RAG'}
                      </Button>
                    </Tooltip>
                  </ButtonGroup>
                </Box>
              </Box>
              <TextField
                fullWidth
                value={inhalt}
                onChange={(e) => setInhalt(e.target.value)}
                multiline
                rows={10}
                placeholder="Schreiben Sie hier den Haupttext Ihres Briefes. Sie können den Text anschließend mit KI oder AnythingLLM verbessern lassen."
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: 'inherit',
                  }
                }}
              />
            </Box>

            <TextField
              fullWidth
              label="Fußzeile / Grußformel"
              value={fusszeile}
              onChange={(e) => setFusszeile(e.target.value)}
              multiline
              rows={3}
              placeholder="z.B. Mit freundlichen Grüßen"
              sx={{ mb: 3 }}
            />
          </Box>
        </Box>

        {/* Right Side: Sidebar with Textbausteine */}
        <Box sx={{ flex: '1 1 25%', height: '100%', minWidth: 300, overflow: 'auto', backgroundColor: 'background.paper' }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Textbausteine
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {/* Textbausteine Suche */}
            <Autocomplete
              options={textbausteine}
              getOptionLabel={(option) => option.bausteinName || ''}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {option.bausteinName}
                    </Typography>
                    {option.kategorie && (
                      <Chip size="small" label={option.kategorie} sx={{ mr: 1, mt: 0.5 }} />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {option.kurzerText?.substring(0, 80)}
                      {option.kurzerText && option.kurzerText.length > 80 ? '...' : ''}
                    </Typography>
                  </Box>
                </Box>
              )}
              onChange={(_, value) => handleInsertTextbaustein(value)}
              loading={textbausteinLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Textbaustein suchen..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <TextFieldsIcon color="action" sx={{ mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {textbausteinLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              fullWidth
              clearOnBlur
              blurOnSelect
              value={null}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Wählen Sie einen Textbaustein aus, um ihn am Ende des Briefinhalts einzufügen
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Kategorien als Chips */}
            <Typography variant="subtitle2" gutterBottom>
              Nach Kategorie filtern
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
              {Array.from(new Set(textbausteine.map(t => t.kategorie).filter(Boolean))).map((kategorie) => (
                <Chip
                  key={kategorie}
                  label={kategorie}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    // Fokus auf Autocomplete setzen und Kategorie filtern
                    // Für jetzt zeigen wir einfach die Kategorie an
                  }}
                />
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Kürzlich verwendete oder beliebte Textbausteine */}
            <Typography variant="subtitle2" gutterBottom>
              Verfügbare Textbausteine ({textbausteine.length})
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {textbausteine.slice(0, 10).map((textbaustein) => (
                <Box
                  key={textbaustein.bausteinID}
                  sx={{
                    p: 1,
                    mb: 1,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onClick={() => handleInsertTextbaustein(textbaustein)}
                >
                  <Typography variant="body2" fontWeight="bold">
                    {textbaustein.bausteinName}
                  </Typography>
                  {textbaustein.kategorie && (
                    <Chip size="small" label={textbaustein.kategorie} sx={{ mt: 0.5 }} />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {textbaustein.kurzerText?.substring(0, 60)}
                    {textbaustein.kurzerText && textbaustein.kurzerText.length > 60 ? '...' : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DokumentNeu;
